// @flow
'use strict'

const {
  getCurrentPageSize,
  getPaginationData,
  selectCurrentRecordIndex,
  selectDefaultDraft,
  isWriteOnly,
  getNumberOfPagesToShow
} = require('../dataset-controller/rootReducer')
const actionTypes = require('./actionTypes')
const queue = require('../actions-queue/addToQueue')
const queryResults = require('../helpers/queryResults')
const { verboseEvent } = require('../dataset-api/verbosity')

/*::
import typeof { WixDataRecord } from 'wix-data-core'
import type { Connection } from '../viewer-app-module/platformTypes'
import type { Record, RecordStore } from '../record-store/types'
import type { AdapterApi } from '../components/types'
*/

const setCurrentIndex = (
  index /*: number */,
  suppressRefreshView /*: boolean */ = false
) => ({
  type: actionTypes.GO_TO_INDEX,
  index,
  suppressRefreshView
})

const refreshCurrentRecord = () => ({
  type: actionTypes.REFRESH_CURRENT_RECORD
})

const refreshCurrentView = () => ({
  type: actionTypes.REFRESH_CURRENT_VIEW
})

const setCurrentRecord = (record /*: Record */, recordIndex /*: number */) => ({
  type: actionTypes.SET_CURRENT_RECORD,
  record,
  recordIndex
})

const updateFields = (
  fieldsToUpdate /*: { [fieldName: string]: * } */,
  updateSource /*: string | void */
) => ({
  type: actionTypes.UPDATE_FIELDS,
  fieldsToUpdate,
  updateSource
})

const incrementNumOfPagesToShow = () => ({
  type: actionTypes.INCREMENT_NUM_PAGES_TO_SHOW
})

const nextPage = () => ({
  type: actionTypes.GO_TO_NEXT_PAGE
})

const previousPage = () => ({
  type: actionTypes.GO_TO_PREVIOUS_PAGE
})

const loadPage = (pageNumber /*: number */) => ({
  type: actionTypes.LOAD_PAGE,
  pageNumber
})

const revert = () => ({
  type: actionTypes.REVERT_CHANGES
})

const flushDraft = () => ({
  type: actionTypes.SAVE_RECORD
})

const setDefaultRecord = (record /*: WixDataRecord */) => ({
  type: actionTypes.SET_DEFAULT_RECORD,
  record
})

const newRecord = (atIndex /*: number */) => ({
  type: actionTypes.NEW_RECORD,
  atIndex
})

const remove = () => ({
  type: actionTypes.REMOVE_CURRENT_RECORD
})

const reInitWriteOnly = () => newRecord(0)

const initWriteOnly = (isScoped /*: ?boolean */) =>
  isScoped ? setCurrentIndex(0) : newRecord(0)

const doFetch = async (
  recordStore /*: RecordStore */,
  fromIndex /*: number */,
  length /*: number */,
  byRefField /*: string | void */
) => {
  const fetchedItems = await recordStore(byRefField).matchWith({
    Error: () => queryResults.Empty(),
    Ok: ({ value: service }) => service.getRecords(fromIndex, length)
  })

  return fetchedItems.get()
}

const fetchCurrentPage = (recordStore /*: RecordStore */, state /*: {} */) => {
  const pageSize = getCurrentPageSize(state) * getNumberOfPagesToShow(state)
  const { offset } = getPaginationData(state)

  return doFetch(recordStore, offset, pageSize)
}

const updateComponentsBasedOnRecord = (adapterApi /*: () => AdapterApi */) => (
  componentIdToExcludeFromUpdatingComponentsBasedOnRecord /*: string | void */,
  updatedFields /*: string[] | void */
) => {
  adapterApi().currentRecordModified(
    updatedFields,
    componentIdToExcludeFromUpdatingComponentsBasedOnRecord
  )
}
exports.updateComponentsBasedOnRecord = updateComponentsBasedOnRecord

exports.refresh = () =>
  queue(async function(
    dispatch,
    getState,
    { adapterApi, eventListeners: { fireEvent }, recordStore }
  ) {
    await recordStore().chain(async service => {
      service.resetDraft(
        selectCurrentRecordIndex(getState()),
        selectDefaultDraft(getState())
      )
      service.reset()

      if (isWriteOnly(getState())) {
        service.newRecord(0, selectDefaultDraft(getState()))
      }
      await dispatch(setCurrentIndex(0, true))

      const componentUpdatePromise = Promise.all(adapterApi().recordSetLoaded())
      verboseEvent('currentIndexChanged', 0)
      fireEvent('currentIndexChanged', 0)
      await componentUpdatePromise
    })
  })

exports.fetchCurrentPage = fetchCurrentPage
exports.doFetch = doFetch
exports.flushDraft = flushDraft
exports.incrementNumOfPagesToShow = incrementNumOfPagesToShow
exports.nextPage = nextPage
exports.previousPage = previousPage
exports.loadPage = loadPage
exports.refreshCurrentRecord = refreshCurrentRecord
exports.refreshCurrentView = refreshCurrentView
exports.revert = revert
exports.setCurrentIndex = setCurrentIndex
exports.setCurrentRecord = setCurrentRecord
exports.setDefaultRecord = setDefaultRecord
exports.updateFields = updateFields
exports.remove = remove
exports.newRecord = newRecord
exports.reInitWriteOnly = reInitWriteOnly
exports.initWriteOnly = initWriteOnly



// WEBPACK FOOTER //
// ./records/actions.js